import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { NextPage } from 'next'
import Head from 'next/head'
import Link from 'next/link'

import { cx } from '@linaria/core'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useFoodSWRInfinite } from '~/api-client/food_api'
import { ChatSpace } from '~/api-definition/definition'
import { AppImage } from '~/components/data-display/AppImage'
import { ListContentLoader } from '~/components/data-display/Loading'
import { UnreadBadge } from '~/components/data-display/UnreadBadge'
import { TextField } from '~/components/data-input/TextField'
import { ShowOnly } from '~/components/food_app/ShowOnly'
import { DinnerSummary } from '~/components/food_app/dinner/DinnerSummary'
import { UserWarningStatusIconButton } from '~/components/food_app/user/UserWarningStatus'
import {
  DeleteIcon,
  GuestIcon,
  MessagePlusIcon,
  SearchIcon,
  UserIcon,
} from '~/components/icons'
import { Container } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { optLast } from '~/libs/array'
import {
  chatDmName,
  chatGroupMemberCountText,
  chatGroupName,
  chatLatestMessageOrEventAt,
  chatSpaceStatusLine,
} from '~/libs/chat'
import { userProfileIcon, userProfileIconOpt } from '~/libs/user'

const Page: NextPage = () => {
  const chatSpaces = useFoodSWRInfinite(
    '/chat/chat_spaces',
    {},
    (_, previousPageData) => {
      return {
        count: config.pageSize.chatSpaceList,
        cursor_next: optLast(previousPageData?.spaces)?.chatSpaceId,
      }
    }
  )

  const [searchQuery, setSearchQuery] = useState('')
  const [newChatSpace, setNewChatSpace] = useState(false)

  const searchQueryInputRef = useRef<HTMLInputElement | null>(null)
  const clearSearchQuery = useCallback(() => {
    if (searchQueryInputRef.current) {
      searchQueryInputRef.current.value = ''
      setSearchQuery('')
    }
  }, [])
  const onChangeSearchQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )

  const spaces = useMemo(() => {
    const spaces: ChatSpace[] = []
    chatSpaces.data?.map((resp) => {
      resp.spaces.map((space) => {
        spaces.push(space)
      })
    })
    return spaces
  }, [chatSpaces.data])
  useEffect(() => {
    console.log(chatSpaces)
  }, [chatSpaces])

  return (
    <div className="min-h-full">
      <Head>
        <title>メッセージ一覧</title>
      </Head>

      <Header>
        <div />
        メッセージ一覧
        <button
          onClick={() => {
            window.scrollTo(0, 0)
            setNewChatSpace((v) => !v)
          }}
        >
          <MessagePlusIcon className="fill-white" />
        </button>
      </Header>

      <div className={cx(!newChatSpace && 'hidden')}>
        <div className="flex items-stretch justify-between px-6 py-4 text-sm font-bold bg-function-cv2 gap-x-4">
          <div className="flex items-center justify-center flex-1 gap-4 text-center">
            <UserIcon className="fill-text h-[27px] w-[22px]" />
            <Link href="/chat_spaces/new/dm">
              個別チャット
              <br />
              を作成
            </Link>
          </div>

          <div className="w-[1px] bg-gray flex-0" />

          <div className="flex items-center justify-center flex-1 gap-4 text-center">
            <GuestIcon className="fill-text h-[27px] w-[30px]" />
            <Link href="/chat_spaces/new/group">
              グループチャット
              <br />
              を作成
            </Link>
          </div>
        </div>
      </div>
      <Container>
        <div className="mx-4 md:mx-0">
          <ShowOnly foodEnvs={['sandbox']}>
            {(spaces.length ?? 0) > 0 && (
              <div
                className={cx(
                  'flex gap-x-2 items-center mt-4',
                  newChatSpace && 'hidden'
                )}
              >
                <div className="flex-grow">
                  <TextField
                    leftIcon={<SearchIcon className="w-4 h-4 fill-text" />}
                    inputProps={{
                      placeholder: '名前、ユーザーIDから検索する',
                      onChange: onChangeSearchQuery,
                      ref: searchQueryInputRef,
                    }}
                  />
                </div>
                {searchQuery && (
                  <button onClick={clearSearchQuery}>
                    <DeleteIcon className="w-5 h-5 fill-text" />
                  </button>
                )}
              </div>
            )}
          </ShowOnly>

          {chatSpaces.data != null && spaces.length === 0 && (
            <div className="text-xs text-center leading-5 mt-[30vh]">
              <p>
                メッセージがありません。
                <br />
                新しくチャットを開始したい場合は、
                <br />
                右上の＋ボタンから作成できます。
              </p>
            </div>
          )}

          {chatSpaces.isLoading && <ListContentLoader />}

          <InfiniteScroll
            className="flex flex-col w-full mx-auto"
            dataLength={spaces?.length ?? 0}
            next={() => {
              chatSpaces.setSize((size) => size + 1)
            }}
            hasMore={
              (chatSpaces.data &&
                chatSpaces.data[chatSpaces.data.length - 1]?.spaces.length !==
                  0) ??
              false
            }
            loader={<div className="loader" key={0} />}
          >
            {spaces?.map((chatSpace, i, chatSpaces) => {
              return (
                <div
                  key={chatSpace.chatSpaceId}
                  className={cx(
                    i !== chatSpaces.length - 1 && 'border-b-[1px] border-gray'
                  )}
                >
                  <Link href={`/chat_spaces/${chatSpace.chatSpaceId}`}>
                    <div className={cx('py-5')}>
                      <div className="flex gap-4 overflow-hidden">
                        <div className="min-w-[54px] relative">
                          {chatSpace.dm && (
                            <AppImage
                              variant={{
                                type: 'profile',
                                user: chatSpace.dm.pairMember.user,
                              }}
                              width={45}
                              height={45}
                              rounded="full"
                              alt={`${chatSpace.dm.pairMember.user.displayName}さんのプロフィール画像`}
                              src={userProfileIcon(
                                chatSpace.dm.pairMember.user
                              )}
                            />
                          )}
                          {chatSpace.group &&
                            chatSpace.group.groupIcon == null &&
                            chatSpace.group.members.length == 1 && (
                              <AppImage
                                width={45}
                                height={45}
                                variant={{
                                  type: 'profile',
                                  user: chatSpace.group.members[0]?.user,
                                }}
                                rounded="full"
                                alt={`${chatSpace.group.groupName}グループ参加者のアイコン`}
                                src={userProfileIconOpt(
                                  chatSpace.group.members[0]?.user
                                )}
                              />
                            )}
                          {chatSpace.group &&
                            chatSpace.group.groupIcon == null &&
                            chatSpace.group.members.length >= 2 && (
                              <div className="absolute bottom-0 left-0">
                                <AppImage
                                  width={30}
                                  height={30}
                                  variant={{
                                    type: 'profile',
                                    user: chatSpace.group.members[0]?.user,
                                  }}
                                  rounded="full"
                                  alt={`${chatSpace.group.groupName}グループ参加者のアイコン`}
                                  src={userProfileIconOpt(
                                    chatSpace.group.members[0]?.user
                                  )}
                                />
                              </div>
                            )}
                          {chatSpace.group &&
                            chatSpace.group.groupIcon == null &&
                            chatSpace.group.members.length >= 2 && (
                              <div className="box-content absolute top-0 right-0 border-4 border-white rounded-full z-5">
                                <AppImage
                                  width={30}
                                  height={30}
                                  variant={{
                                    type: 'profile',
                                    user: chatSpace.group.members[1]?.user,
                                  }}
                                  rounded="full"
                                  alt={`${chatSpace.group.groupName}グループ参加者のアイコン`}
                                  src={userProfileIconOpt(
                                    chatSpace.group.members[1]?.user
                                  )}
                                />
                              </div>
                            )}
                          {chatSpace.group &&
                            chatSpace.group.groupIcon != null && (
                              <AppImage
                                alt={`${chatSpace.group.groupName}グループのアイコン`}
                                src={{
                                  try: chatSpace.group.groupIcon.imageUrl,
                                  fallback:
                                    config.noImageUrl.restaurantCoverImage,
                                }}
                                width={45}
                                height={45}
                                variant={{
                                  type: 'cover',
                                  coverPosition: 'center',
                                }}
                                rounded="md"
                              />
                            )}
                        </div>
                        <div className="flex-grow flex-shrink min-w-0">
                          <div className="text-sm font-bold">
                            {chatSpace.dm ? (
                              <div className="flex items-center gap-x-2">
                                <span className="truncate">
                                  {chatDmName(chatSpace.dm)}
                                </span>
                                {chatSpace.dm.pairMember.user.warningStatus !=
                                  null && (
                                  <UserWarningStatusIconButton
                                    userWarningStatus={
                                      chatSpace.dm.pairMember.user.warningStatus
                                    }
                                  />
                                )}
                                <span className="truncate font-normal text-[#6C6C6C] text-[13px]">
                                  @{chatSpace.dm.pairMember.user.userName}
                                </span>
                                <div className="text-xs text-[#AEAEAE] font-normal whitespace-nowrap ml-auto">
                                  {chatLatestMessageOrEventAt(chatSpace)}
                                </div>
                              </div>
                            ) : chatSpace.group != null ? (
                              <div className="flex">
                                <div className="truncate">
                                  {chatGroupName(chatSpace.group)}
                                </div>
                                {chatGroupMemberCountText(chatSpace.group)}
                                <div className="text-xs text-[#AEAEAE] font-normal whitespace-nowrap ml-auto">
                                  {chatLatestMessageOrEventAt(chatSpace)}
                                </div>
                              </div>
                            ) : (
                              <span />
                            )}
                          </div>
                          <div className="flex justify-between mt-1 items-center h-[22px]">
                            <div className="text-[13px] leading-[22px] truncate min-w-0">
                              {chatSpaceStatusLine(chatSpace)}
                            </div>

                            <UnreadBadge
                              count={
                                chatSpace.unreadMessageCount +
                                chatSpace.unreadEventCount
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                  {chatSpace.dinner != null && (
                    <div className="pb-4">
                      <DinnerSummary
                        invert
                        showDate
                        showParticipationStatus
                        hideRecruitmentProgress
                        hidePrivacyType
                        dinner={chatSpace.dinner}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </InfiniteScroll>
        </div>
      </Container>
      <Footer />
    </div>
  )
}

export default Page
